import { mainApi } from '../../../../api/mainApi';
import { middlewareApi } from '../../../middleware/middlewareApi';
import { FaqUpdate, FaqCreate } from '../interfaces/faq-payload';
import { Faq, FaqList } from '../interfaces/faqs-list.interfaces';
import { setFaqDetail } from '../slices/faqSlice';

export const faqsTags = middlewareApi.enhanceEndpoints({
  addTagTypes: ['Faqs'],
});
// Define a service using a base URL and expected endpoints
export const faqApi = faqsTags.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * @GET all faqs
     */
    getFaqs: builder.query<
      FaqList,
      {
        profileId: number;
        page?: number;
        urlRelative?: string;
        perPage?: number;
        search?: string;
        sort?: string;
        type_sort?: string;
      }
    >({
      queryFn: async (
        { profileId, page, search, perPage, urlRelative, sort, type_sort },
        { dispatch }
      ) => {
        try {
          const { data } = await mainApi.get<FaqList>('/faqs', {
            params: {
              user_profile_id: profileId,
              page,
              url_relative: urlRelative,
              size: perPage,
              ...(sort && { sort }),
              type_sort: type_sort || 'asc',
              search: search,
            },
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (result) =>
        result
          ? [
              ...result?.data?.map(({ id }) => ({ type: 'Faqs', id } as const)),
              { type: 'Faqs', id: 'LIST' },
            ]
          : [{ type: 'Faqs', id: 'LIST' }],
    }),

    /**
     * @GET filter faqs
     */
    filterFaqs: builder.query<
      FaqList,
      {
        page?: number;
        perPage?: number;
        typeSort?: string;
        sort?: string;
        profileId: number;
        dataAll?: string;
        urlRelative?: string;
        search?: string;
      }
    >({
      queryFn: async ({
        profileId,
        page,
        search,
        perPage,
        urlRelative,
        dataAll,
        typeSort,
        sort,
      }) => {
        try {
          const { data } = await mainApi.get<FaqList>('/faqs', {
            params: {
              user_profile_id: profileId,
              page,
              data: dataAll,
              url_relative: urlRelative,
              size: perPage,
              sort: sort,
              type_sort: typeSort,
              search: search,
            },
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
    }),

    getFaqById: builder.query<
      Faq,
      {
        profileId: number;
        faqId: number;
      }
    >({
      queryFn: async ({ profileId, faqId }, { dispatch }) => {
        try {
          const { data } = await mainApi.get<Faq>(`/faqs/${faqId}`, {
            params: {
              user_profile_id: profileId,
            },
          });

          dispatch(setFaqDetail(data));

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
    }),

    getFaqByModule: builder.query<
      FaqList,
      {
        profileId: number;
        page?: number;
        urlRelative?: string;
        perPage?: number;
        search?: string;
        sort?: string;
        type_sort?: string;
      }
    >({
      queryFn: async (
        { profileId, page, search, perPage, urlRelative, sort, type_sort },
        { dispatch }
      ) => {
        try {
          const { data } = await mainApi.get<FaqList>(`/faqs/modules/${search}`, {
            params: {
              user_profile_id: profileId,
              page,
              url_relative: urlRelative,
              size: perPage,
              ...(sort && { sort }),
              type_sort: type_sort || 'asc',
              search: search,
            },
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
    }),

    createFaq: builder.mutation<FaqCreate, { payload: FaqCreate; profileId: number }>({
      queryFn: async ({ profileId, payload }) => {
        try {
          const form = new FormData();
          form.append('files[]', payload.file);
          form.append('type_document', '14');
          form.append('user_profile_id', profileId.toString());
          form.append('faq_keyword', payload.faq_keyword);
          form.append('faq_question', payload.faq_question);
          form.append('faq_answer', payload.faq_answer);
          form.append('faq_link_video', payload.faq_link_video);
          form.append('faq_tags', payload.faq_tags.join(','));
          form.append('faq_module', payload.faq_module);

          const { data } = await mainApi.post('/faqs', form, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: [{ type: 'Faqs', id: 'LIST' }],
    }),

    updateFaq: builder.mutation<
      FaqUpdate,
      { faqId: number; payload: FaqUpdate; profileId: number }
    >({
      queryFn: async ({ profileId, payload, faqId }) => {
        try {
          const form = new FormData();
          form.append('files[]', payload.file!);
          form.append('type_document', '14');
          form.append('user_profile_id', profileId.toString());
          form.append('faq_keyword', payload.faq_keyword);
          form.append('faq_question', payload.faq_question);
          form.append('faq_answer', payload.faq_answer);
          form.append('faq_link_video', payload.faq_link_video);
          form.append('faq_tags', payload.faq_tags.join(','));
          form.append('faq_module', payload.faq_module);
          
          const { data } = await mainApi.post(`/faqs/${faqId}`, form, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: [{ type: 'Faqs', id: 'LIST' }],
    }),

    deleteFaq: builder.mutation<FaqUpdate, { faqId: number; profileId: number }>({
      queryFn: async ({ profileId, faqId }) => {
        try {
          const { data } = await mainApi.delete(`/faqs/${faqId}`, {
            data: {
              user_profile_id: profileId,
            },
          });
          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: [{ type: 'Faqs', id: 'LIST' }],
    }),

    getTagsFaqsAvailables: builder.query<string[], { profileId: number; faqKeyword?: string }>({
      queryFn: async ({ profileId, faqKeyword }) => {
        try {
          const { data } = await mainApi.get<string[]>(`/faqs/tags/list`, {
            params: {
              user_profile_id: profileId,
              faq_keyword: faqKeyword,
            },
          });
          return { data: Array.isArray(data) ? data : [] };
        } catch (error: any) {
          return { error };
        }
      },
    }),
    faqSatisfaction: builder.mutation<
      void,
      {
        faqId: number;
        satisfaction: 'faq_useful' | 'faq_not_useful';
        profileId: number;
      }
    >({
      queryFn: async ({ profileId, faqId, satisfaction }) => {
        try {
          const { data } = await mainApi.put(`/faqs/${faqId}/satisfaction`, {
            user_profile_id: profileId,
            satisfaction,
          });
          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: [{ type: 'Faqs', id: 'LIST' }],
    }),
    faqVisit: builder.mutation<void, { faqId: number; profileId: number }>({
      queryFn: async ({ profileId, faqId }) => {
        try {
          const { data } = await mainApi.put(`/faqs/${faqId}/visit`, {
            user_profile_id: profileId,
          });
          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: [{ type: 'Faqs', id: 'LIST' }],
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetFaqsQuery,
  useCreateFaqMutation,
  useGetFaqByIdQuery,
  useGetFaqByModuleQuery,
  useFilterFaqsQuery,
  useFaqSatisfactionMutation,
  useFaqVisitMutation,
  useGetTagsFaqsAvailablesQuery,
  useDeleteFaqMutation,
  useUpdateFaqMutation,
} = faqApi;
